"use client";

import { cn } from "@/lib/utils";
import { Dialog as DialogPrimitive } from "radix-ui";
import type * as React from "react";
import { tv, type VariantProps } from "tailwind-variants";

const dialogVariants = tv({
  base: "data-open:animate-in data-closed:animate-out",
  variants: {
    position: {
      "top-left": "top-0 left-0",
      "top-right": "top-0 right-0",
      "bottom-left": "bottom-0 left-0",
      "bottom-right": "bottom-0 right-0",
      center: "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-auto",
    },
    animate: {
      "from-right":
        "data-closed:slide-out-to-right-full data-open:slide-in-from-right-full",
      "from-left":
        "data-closed:slide-out-to-left-full data-open:slide-in-from-left-full",
      "from-top":
        "data-closed:slide-out-to-top-full data-open:slide-in-from-top-full",
      "from-bottom":
        "data-closed:slide-out-to-bottom-full data-open:slide-in-from-bottom-full",
      "fade-in": "data-closed:fade-out-0 data-open:fade-in-0",
    },
  },
  defaultVariants: {
    position: "center",
    animate: "fade-in",
  },
});

function Dialog({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}
function DialogTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}
function DialogPortal({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}
function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        "data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 fixed inset-0 isolate z-50 bg-black/40 duration-300",
        className,
      )}
      {...props}
    />
  );
}

function DialogContent({
  className,
  children,
  position,
  animate,
  overlayClassName,
  showOverlay = true,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> &
  VariantProps<typeof dialogVariants> & {
    overlayClassName?: string;
    showOverlay?: boolean;
  }) {
  return (
    <DialogPortal>
      {showOverlay && <DialogOverlay className={overlayClassName} />}
      <DialogPrimitive.Content
        data-slot="dialog-content"
        data-position={position}
        className={cn(
          "fixed top-0 right-0 z-50 grid w-full max-w-sm content-start overflow-y-auto scroll-smooth bg-white duration-300 ease-in-out",
          dialogVariants({ position, animate }),
          className,
        )}
        {...props}
      >
        {children}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

function DialogHeader({
  className,
  children,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn(
        "sticky top-0 z-10 flex min-h-17 flex-row items-center justify-between bg-white/90 p-4 backdrop-blur-sm",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

function DialogFooter({
  className,
  children,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "bg-secondary sticky bottom-0 z-10 flex flex-col gap-4 p-4 backdrop-blur-sm",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn(className)}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(
        "text-muted-foreground *:[a]:hover:text-foreground text-xs/relaxed *:[a]:underline *:[a]:underline-offset-3",
        className,
      )}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
